import { useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useNavPreferences } from "@/hooks/useNavPreferences";
import { useAiHelperPrefs } from "@/lib/ai-helper-prefs";
import { useContractorMembership } from "@/hooks/useContractorMembership";
import { hasPermission, type PermissionContext } from "@/lib/permissions";
import {
  authorizedCatalog, defaultBottomNavIds, defaultSidebarIds, DAR_NAV_ID, BOTTOM_NAV_MAX,
  type NavItem,
} from "@/lib/nav-catalog";
import {
  overflowItems, resolveBottomNav, resolveDarPlacement, resolveSidebar,
  type DarPlacement, type NavPreferences,
} from "@/lib/nav-prefs";

/**
 * Effective navigation = authorized catalog ∩ user preference.
 *
 * Recomputed on every render of the permission context (role, active org, user,
 * feature flags, entitlement), so a revoked permission removes items on the next
 * evaluation without any manual reset. Route authorization is unchanged.
 */
export type EffectiveNav = {
  ctx: PermissionContext;
  /** All authorized items (catalog order). */
  authorizedAll: NavItem[];
  authorizedBottom: NavItem[];
  authorizedSidebar: NavItem[];
  bottomItems: NavItem[];
  bottomIds: string[];
  overflow: NavItem[];
  sidebarItems: NavItem[];
  sidebarIds: string[];
  darAuthorized: boolean;
  darPlacement: DarPlacement;
  defaults: { bottom: string[]; sidebar: string[] };
  prefs: NavPreferences;
  loading: boolean;
  save: (patch: Partial<NavPreferences>) => Promise<void>;
  maxBottom: number;
};

export function useEffectiveNav(): EffectiveNav {
  const { user, currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const flags = useFeatureFlags();
  const ent = useEntitlement();
  const { prefs, loading, save } = useNavPreferences();
  const [aiPrefs] = useAiHelperPrefs();
  const aiFloating = aiPrefs.floating;
  const isContractorMember = useContractorMembership();

  const hasPaidPlan = ent.exempt || (ent.plan_key !== null && ent.plan_key !== "free");

  const ctx: PermissionContext = useMemo(
    () => ({ role: currentRole, isPlatformAdmin, flags, hasPaidPlan, isContractorMember }),
    [currentRole, isPlatformAdmin, flags, hasPaidPlan, isContractorMember],
  );

  const authorizedAll = useMemo(() => authorizedCatalog(ctx), [ctx]);
  const authorizedBottom = useMemo(() => authorizedCatalog(ctx, "bottom_nav"), [ctx]);
  const authorizedSidebar = useMemo(() => authorizedCatalog(ctx, "sidebar"), [ctx]);
  const defaults = useMemo(
    () => ({ bottom: defaultBottomNavIds(ctx), sidebar: defaultSidebarIds(ctx) }),
    [ctx],
  );

  const darAuthorized = hasPermission(ctx, "dar.create");

  const bottom = useMemo(
    () => resolveBottomNav(prefs.bottom_nav_order, authorizedBottom, defaults.bottom),
    [prefs.bottom_nav_order, authorizedBottom, defaults.bottom],
  );
  const sidebar = useMemo(
    () => resolveSidebar(prefs.sidebar_order, authorizedSidebar, defaults.sidebar),
    [prefs.sidebar_order, authorizedSidebar, defaults.sidebar],
  );
  const dar = useMemo(
    () => resolveDarPlacement(prefs.dar_orb_placement, darAuthorized),
    [prefs.dar_orb_placement, darAuthorized],
  );

  // ---- Repair saved preferences, notify once, and audit the change ----
  const repairedRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !user || !currentOrg || !flags.loaded) return;
    const needsRepair = bottom.repaired || sidebar.repaired || dar.repaired;
    if (!needsRepair) return;

    const dropped = Array.from(new Set([...bottom.dropped, ...sidebar.dropped]));
    const signature = `${currentOrg.id}:${dropped.join(",")}:${bottom.repaired}:${sidebar.repaired}:${dar.repaired}`;
    if (repairedRef.current === signature) return;
    repairedRef.current = signature;

    void save({
      bottom_nav_order: bottom.ids,
      sidebar_order: sidebar.ids,
      dar_orb_placement: dar.placement,
    });

    if (dropped.length > 0 || dar.repaired) {
      const noticeKey = `missionready.nav.repair.${signature}`;
      let alreadyShown = false;
      try { alreadyShown = window.sessionStorage.getItem(noticeKey) === "1"; } catch { /* ignore */ }
      if (!alreadyShown) {
        try { window.sessionStorage.setItem(noticeKey, "1"); } catch { /* ignore */ }
        toast("Your navigation was updated because your access changed.", {
          description: "Items you can no longer use were removed. Your remaining order was kept.",
          action: { label: "Dismiss", onClick: () => undefined },
        });
      }

      const affected: string[] = [];
      if (bottom.repaired) affected.push("bottom_nav");
      if (sidebar.repaired) affected.push("sidebar");
      if (dar.repaired) affected.push("dar_placement");
      void supabase.from("audit_log").insert({
        organization_id: currentOrg.id,
        actor_id: user.id,
        action: "nav_preferences_repaired",
        target_type: "user_nav_preferences",
        target_id: user.id,
        metadata: {
          dropped_items: dropped,
          affected_navigation_type: affected,
          trigger: dropped.length > 0 ? "feature_access_removed" : "permission_revoked",
        },
      });
    }
  }, [loading, user?.id, currentOrg?.id, flags.loaded, bottom, sidebar, dar, save]);

  // Reset the repair de-dupe when the identity/org context changes.
  useEffect(() => { repairedRef.current = null; }, [user?.id, currentOrg?.id]);

  return {
    ctx,
    authorizedAll,
    authorizedBottom,
    authorizedSidebar,
    bottomItems: bottom.items,
    bottomIds: bottom.ids,
    overflow: overflowItems(bottom.ids, authorizedAll),
    // The AI Helper is presented either in navigation or as the floating orb —
    // never both. This is placement only; authorization is unchanged.
    sidebarItems: aiFloating ? sidebar.items.filter((i) => i.id !== "ai_helper") : sidebar.items,
    sidebarIds: sidebar.ids,
    darAuthorized,
    darPlacement: dar.placement,
    defaults,
    prefs,
    loading,
    save,
    maxBottom: BOTTOM_NAV_MAX,
  };
}

export { DAR_NAV_ID };
